import { yupResolver } from '@hookform/resolvers/yup';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import Box from '@mui/system/Box';
import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
	MultiSelectElement,
	SelectElement,
	TextFieldElement,
} from 'react-hook-form-mui';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { SearchRequest, SearchReturnType } from '../../api/CoreSearchApi';
import { useConfigContext } from '../../context/config';
import {
	RESET_BUTTON,
	SEARCH_BUTTON,
	SEARCH_FILTER_BY_PRODUCT,
	SEARCH_FILTER_BY_TYPE,
} from '../../data/labelsAndMessages';
import { CoreSearchResultList } from './CoreSearchResultList';

const returnTypes = {
	'': 'None',
	policyholder: 'Policyholder',
	policy: 'Policy',
	claim: 'Claim',
	payment: 'Payment',
	quote: 'Quote',
	premium_report: 'Premium Report',
};

const returnTypeOptions = Object.entries(returnTypes).map((returnTypes) => ({
	id: returnTypes[0],
	label: returnTypes[1],
}));

const schema = () =>
	yup.object().shape({
		searchString: yup.string().nullable().required('Required'),
		returnType: yup.string().nullable(),
		products: yup.array().of(yup.string()).nullable(),
	});

const useGetUrlSearchRequest = () => {
	const locationSearch = useLocation().search;
	const urlParams = new URLSearchParams(locationSearch);
	const request: SearchRequest = {
		searchString: urlParams.get('searchString') ?? '',
	};
	if (urlParams.get('returnType')) {
		request.returnType =
			(urlParams.get('returnType') as SearchReturnType) || '';
	}
	if (urlParams.get('products')) {
		request.products =
			(JSON.parse(urlParams.get('products')) as string[]) || [];
	}

	return request;
};

export default function CoreAdvancedSearchView() {
	const searchRequestFromUrl = useGetUrlSearchRequest();
	const navigate = useNavigate();
	const onApply = ({ searchString, returnType, products }: SearchRequest) => {
		const params = new URLSearchParams({});

		if (searchString) {
			params.set('searchString', searchString + '');
		}
		if (returnType) {
			params.set('returnType', returnType + '');
		}
		if (products) {
			params.set('products', JSON.stringify(products));
		}
		navigate(location.pathname + '?' + params.toString());
	};

	return (
		<>
			<SearchFilters
				searchString={searchRequestFromUrl?.searchString}
				returnType={searchRequestFromUrl?.returnType}
				products={searchRequestFromUrl?.products}
				onApply={onApply}
			/>
			<Box>
				<CoreSearchResultList searchRequest={searchRequestFromUrl} />
			</Box>
		</>
	);
}

interface SearchFilterProps {
	searchString: string;
	returnType: SearchReturnType;
	products: string[];
	onApply: (data: SearchRequest) => void;
}

const SearchFilters = (props: SearchFilterProps) => {
	const { searchString, returnType, products, onApply } = props;
	const navigate = useNavigate();
	const configData = useConfigContext();
	const productOptions =
		configData?.productConfigs
			?.map((config) => config.name)
			?.filter((name) => name !== 'global') || [];

	const { handleSubmit, control, reset } = useForm<SearchRequest>({
		resolver: yupResolver(schema()),
		defaultValues: {
			searchString: searchString || '',
			returnType: returnType,
			products: products,
		},
	});

	const onSubmit: SubmitHandler<SearchRequest> = (data) => {
		const { searchString, returnType, products } = data;
		const request = {} as SearchRequest;

		if (searchString) {
			request.searchString = searchString;
		}
		if (returnType) {
			request.returnType = returnType.valueOf() as SearchReturnType;
		}
		if (products) {
			request.products = products.valueOf() as string[];
		}

		onApply(request);
	};

	const resetSearchRequest = () => {
		reset();
		navigate(location.pathname, { replace: true });
	};

	return (
		<Box
			onSubmit={handleSubmit(onSubmit)}
			component='form'
			sx={{ pt: 1, display: 'flex', flexWrap: 'wrap' }}
		>
			<Box sx={{ width: 1, display: 'flex', alignItems: 'center' }}>
				<TextFieldElement
					name='searchString'
					control={control}
					sx={{ width: 1, mr: 2 }}
					autoFocus
					variant='outlined'
					InputProps={{
						startAdornment: <SearchIcon sx={{ pr: 1 }} />,
					}}
				/>
				<Button
					type='submit'
					variant='contained'
					color='secondary'
					sx={{ minWidth: 80 }}
				>
					{SEARCH_BUTTON()}
				</Button>
				<Button
					variant='text'
					sx={{ ml: 1, minWidth: 80, color: 'black.main' }}
					onClick={resetSearchRequest}
				>
					{RESET_BUTTON()}
				</Button>
			</Box>
			<Box sx={{ mt: 2, mr: 2, minWidth: 80, maxWidth: 300 }}>
				<SelectElement
					sx={{ width: 1 }}
					name='returnType'
					control={control}
					label={SEARCH_FILTER_BY_TYPE()}
					variant='outlined'
					options={returnTypeOptions}
				/>
			</Box>
			<Box sx={{ mt: 2, maxWidth: 300 }}>
				<MultiSelectElement
					name='products'
					control={control}
					label={SEARCH_FILTER_BY_PRODUCT()}
					variant='outlined'
					options={productOptions}
					showCheckbox
					formControlProps={{ sx: { maxWidth: 300 } }}
				/>
			</Box>
		</Box>
	);
};
